import {
  PathMarker as IconPathMarker,
  PinMap as IconPinMarker,
  PickupMarker,
  ShipperLocation as IconShipperMarker,
} from '@ahamove/icons/map';
import {
  IndexedPathMarker,
  IndexedPickupMarker,
  IndexedPickupPathMarker,
} from '@ahamove/icons/marker';
import { OrderStatus, PointType } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import type { CSSProperties } from 'react';

import type { MarkerIconType, PathWithIndexes } from '../types';

type GetIconProps = {
  path: PathWithIndexes;
  type: MarkerIconType;
  pathsLength: number;
};

export default function useMarkers(isMove?: boolean) {
  const markerSize = {
    height: 48,
    width: 32,
  };

  const markerStyles: CSSProperties = {
    ...markerSize,
    transition: 'transform 0.15s ease-out',
    transform: isMove
      ? 'scale(1.35) translateY(-4px)'
      : 'scale(1) translateY(6px)',
  };

  function getSingleTypeIcon({ path, type }: GetIconProps) {
    const pathClassNames = cn('', {
      '[&>g>path:nth-child(2)]:fill-red-50': path.status === OrderStatus.Failed,
      '[&>g>path:nth-child(2)]:fill-green-50':
        path.status === OrderStatus.Completed,
      '[&>g>path:nth-child(2)]:fill-secondary-50':
        path.action_type === PointType.Pickup,
    });

    if (type === 'supplier-location') {
      return (
        <IconShipperMarker
          style={{
            height: 40,
            width: 32,
          }}
          className={pathClassNames}
        />
      );
    }

    if (type === 'path') {
      return <IconPathMarker style={markerStyles} className={pathClassNames} />;
    }

    if (type === 'pin') {
      return <IconPinMarker style={markerStyles} className={pathClassNames} />;
    }

    if (type === 'booking' && path.indexes[0] <= 0) {
      return <PickupMarker style={markerStyles} />;
    }

    const iconLabel =
      path.indexes.length > 1
        ? `+${path.indexes.length}`
        : `${path.indexes[0]}`;
    return (
      <IndexedPathMarker
        value={iconLabel}
        style={markerStyles}
        className={pathClassNames}
      />
    );
  }

  function getMultiTypeIcon({ path, type, pathsLength }: GetIconProps) {
    const iconLabel =
      path.indexes.length > 1
        ? `+${path.indexes.length}`
        : `${path.indexes[0]}`;
    const actionTypes = path
      .point_type!.split(',')
      .map((item) => item.trim() as PointType);
    const allPickup = actionTypes.every((item) => item === PointType.Pickup);
    const allDropOff = actionTypes.every((item) => item === PointType.DropOff);

    if (allPickup)
      return <IndexedPickupMarker value={iconLabel} style={markerStyles} />;
    if (allDropOff) return getSingleTypeIcon({ path, pathsLength, type });
    return <IndexedPickupPathMarker value={iconLabel} style={markerStyles} />;
  }

  return { getSingleTypeIcon, getMultiTypeIcon };
}
